import { GroupRoles } from '@bp/pim-auth-constants';
import { ModalBottomButtons } from '@bp/ui-components';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { ProfileSelection } from 'components/ProfileSelection/ProfileSelection';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { showSuccessUpdateToast, showUserDbErrorToast } from 'utils/toasts';
import { uniqueByProfileUuid } from 'utils/uniquByUuid';
import { CourseMember } from '../types/types';
import { UpdateCoursesMutationDocument } from './CourseForm';

export const CourseViewersFormFragment = graphql(`
  fragment CourseViewersFormFields on Query {
    course: groups(where: { uuid: $courseUuid }) {
      __typename
      uuid
      viewers {
        __typename
        uuid
        displayName
        organizationRoles
      }
      editors {
        __typename
        uuid
      }
    }
    ...ProfileSelectionFields
  }
`);

type CourseViewersFormType = {
  viewers: CourseMember[];
};

type CourseViewersFormProps = {
  courseUuid: string;
  schoolYearUuid: string;
  onLoad: (isLoading: boolean) => void;
  onClose: () => void;
  query: FragmentType<typeof CourseViewersFormFragment> | undefined;
};

export const CourseViewersForm = ({ courseUuid, schoolYearUuid, onLoad, onClose, query }: CourseViewersFormProps) => {
  const { t } = useTranslation();

  const fragment = getFragmentData(CourseViewersFormFragment, query);

  const course = fragment?.course[0];

  const [, updateCourse] = useMutation(UpdateCoursesMutationDocument);

  const handleSubmit = async (values: CourseViewersFormType, helpers: FormikHelpers<CourseViewersFormType>) => {
    onLoad(true);

    const { viewers } = values;

    const viewerUuids = uniqueByProfileUuid(viewers).map((v) => v.profileUuid);

    const { error } = await updateCourse({
      update: { viewers: viewerUuids },
      uuid: courseUuid,
    });
    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessUpdateToast();
    }

    onLoad(false);
    helpers.resetForm();
    onClose();
  };

  const initialValues: CourseViewersFormType = {
    viewers:
      course?.viewers.map((e) => ({
        ...e,
        profileUuid: e.uuid,
        name: e.displayName ?? '',
        groupRole: GroupRoles.Editor,
      })) ?? [],
  };

  return (
    <Formik<CourseViewersFormType> onSubmit={handleSubmit} initialValues={initialValues}>
      {({ setFieldValue, values }) => (
        <Form>
          <ProfileSelection
            initialMembers={values.viewers.map((m) => ({
              ...m,
              uuid: m.profileUuid,
              organizationRoles: m.organizationRoles,
              selected: false,
            }))}
            excludeMemberUuids={course?.editors.map((e) => e.uuid)}
            onPick={async (picked) => {
              const viewers: CourseMember[] = picked.map((p) => ({
                ...p,
                profileUuid: p.uuid,
                groupRole: GroupRoles.Viewer,
              }));
              await setFieldValue('viewers', viewers);
            }}
            schoolYearUuid={schoolYearUuid}
            query={fragment}
          />

          <ModalBottomButtons
            closeButton={{
              callback: onClose,
            }}
            additionalLeftComponent={t('courses.viewersHint')}
          />
        </Form>
      )}
    </Formik>
  );
};
